import useModal from '__components/Modals/useModal';
import { useCallback, useRef, useState } from 'react';
import { DeactivationModalProps } from '../../components/DeactivationModal';

export type ShowDeactivationModalParams<R = any> = {
  handleSave: () => Promise<R>;
  handleDiscard?: (() => R) | null;
};

export type ShowDeactivationModalWithResultFn<R> = (
  params: ShowDeactivationModalParams<R>
) => Promise<R>;

export const useDeactivationModal = <R = boolean | null>({
  initial,
}: {
  initial: R;
}): {
  deactivationModalProps: DeactivationModalProps;
  showDeactivationModal: (params: ShowDeactivationModalParams) => void;
  showDeactivationModalWithResult: ShowDeactivationModalWithResultFn<R>;
} => {
  const [saver, setSaver] = useState<(() => Promise<any>) | null>(null);
  const [discarder, setDiscarder] = useState<(() => any) | null>(null);

  const state = useRef<{
    ready: boolean;
    result: R;
    closed: boolean;
  }>({
    ready: false,
    result: initial,
    closed: true,
  }).current;

  const [{ onHide, ...deactivationModalProps }, , { show }] = useModal({
    handleSubmit: async () => {
      await saver?.();
    },
    handleHide: () => {
      state.closed = true;
      setSaver(null);
      setDiscarder(null);
    },
  });

  const showDeactivationModal = useCallback(
    ({ handleSave, handleDiscard = null }: ShowDeactivationModalParams) => {
      setSaver(() => handleSave);
      setDiscarder(() => handleDiscard);
      show();
    },
    [show]
  );

  const showDeactivationModalWithResult = useCallback(
    ({
      handleSave,
      handleDiscard = null,
    }: ShowDeactivationModalParams<R>): Promise<R> => {
      state.result = initial;
      state.ready = false;
      state.closed = false;

      showDeactivationModal({
        handleSave: async () => {
          state.result = await handleSave();
          state.ready = true;
        },
        handleDiscard: () => {
          state.ready = true;
          handleDiscard?.();
        },
      });

      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (state.ready || state.closed) {
            clearInterval(interval);
            resolve(state.result);
          }
        }, 10);
      });
    },
    [showDeactivationModal, state, initial]
  );

  const defaultLeftBtnHandler = useCallback(() => {
    discarder?.();
    onHide();
  }, [discarder, onHide]);

  return {
    deactivationModalProps: {
      ...deactivationModalProps,
      onHide,
      defaultLeftBtnHandler,
    },
    showDeactivationModal,
    showDeactivationModalWithResult,
  };
};
